import { useState } from "react";
import { Shield, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { authClient } from "@/lib/auth-client";

interface BackupCodeStepProps {
  onSuccess: () => void;
  onBack: () => void;
}

export const BackupCodeStep = ({ onSuccess, onBack }: BackupCodeStepProps) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;

    setLoading(true);
    try {
      const { data, error } = await authClient.twoFactor.verifyBackupCode({
        code: code.trim(),
        // Note: Better Auth might not support trustDevice on backup codes directly 
        // depending on version, but we'll assume standard flow.
      });

      if (error) {
        throw error;
      }

      toast.success("Verified with backup code");
      onSuccess();
    } catch (error: any) {
      console.error("Backup code verification error:", error);
      toast.error(error.message || "Invalid backup code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="text-center space-y-2">
        <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
          <Shield className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold">Backup Code Recovery</h3>
          <p className="text-sm text-muted-foreground">
            Enter one of your 8-character recovery codes
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="backup-code">Recovery Code</Label>
        <Input
          id="backup-code"
          placeholder="e.g. a1b2-c3d4"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={loading}
          autoFocus
          autoComplete="off"
          className="font-mono text-center uppercase tracking-widest"
        />
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={loading}
          className="flex-1"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button
          type="submit"
          disabled={!code.trim() || loading}
          className="flex-1"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Verifying...
            </>
          ) : (
            "Verify"
          )}
        </Button>
      </div>
    </form>
  );
};
